import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || ''
  });
  const [loading, setLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [activeButton, setActiveButton] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await updateProfile(formData);
    } catch (error) {
      // Error is already handled in auth context
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('Passwords do not match!');
      return;
    }
    
    setPasswordLoading(true);
    setActiveButton('changePassword');
    
    try {
      // Simulate password change API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      alert('Password changed successfully!');
      setShowPasswordModal(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      alert('Failed to change password. Please try again.');
    } finally {
      setPasswordLoading(false);
      setActiveButton(null);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    setActiveButton('deleteAccount');
    
    try {
      // Simulate delete account API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      alert('Account deleted successfully!');
      // Redirect to login or home page
      window.location.href = '/login';
    } catch (error) {
      alert('Failed to delete account. Please try again.');
    } finally {
      setDeleteLoading(false);
      setActiveButton(null);
      setShowDeleteModal(false);
    }
  };

  const handleDownloadOrderHistory = () => {
    setActiveButton('orderHistory');
    
    // Simulate order history download
    const orderData = [
      { orderNumber: 'ORD-101', platform: 'Instagram', followers: 1000, price: 20, date: '2024-01-15' },
      { orderNumber: 'ORD-102', platform: 'Facebook', followers: 500, price: 10, date: '2024-01-10' },
      { orderNumber: 'ORD-103', platform: 'Twitter', followers: 750, price: 15, date: '2024-01-05' }
    ];
    
    const csvContent = "Order Number,Platform,Followers,Price,Date\n" +
      orderData.map(order => `${order.orderNumber},${order.platform},${order.followers},${order.price},${order.date}`).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'order_history.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    
    alert('Order history downloaded successfully!');
    setActiveButton(null);
  };

  const handleExportProfileData = () => {
    setActiveButton('exportProfile');
    
    // Simulate profile data export
    const profileData = {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: formData.phone,
      memberSince: user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A',
      accountType: user?.role || 'User',
      accountStatus: user?.isActive ? 'Active' : 'Inactive',
      emailVerified: user?.emailVerified ? 'Yes' : 'No'
    };
    
    const jsonContent = JSON.stringify(profileData, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'profile_data.json';
    a.click();
    window.URL.revokeObjectURL(url);
    
    alert('Profile data exported successfully!');
    setActiveButton(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-premium-purple-50 to-premium-purple-100 pt-24 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-light-purple-100">
            Profile Settings
          </h1>
          <p className="text-light-purple-400 mt-2">
            Manage your account information and preferences
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Profile Form */}
          <div className="lg:col-span-2">
            <div className="card premium-glow">
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <h2 className="text-lg font-medium text-light-purple-200 mb-4">
                    Personal Information
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-light-purple-400">
                        First Name
                      </label>
                      <div className="mt-1">
                        <input
                          id="firstName"
                          name="firstName"
                          type="text"
                          required
                          value={formData.firstName}
                          onChange={handleChange}
                          className="input-field"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-light-purple-400">
                        Last Name
                      </label>
                      <div className="mt-1">
                        <input
                          id="lastName"
                          name="lastName"
                          type="text"
                          required
                          value={formData.lastName}
                          onChange={handleChange}
                          className="input-field"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-light-purple-400">
                      Email Address
                    </label>
                    <div className="mt-1">
                      <input
                        id="email"
                        type="email"
                        value={user?.email}
                        disabled
                        className="input-field bg-gray-100"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Email cannot be changed. Contact support for assistance.
                      </p>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-light-purple-400">
                      Phone Number
                    </label>
                    <div className="mt-1">
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="input-field"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-6">
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary"
                  >
                    {loading ? 'Updating...' : 'Update Profile'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Account Info */}
          <div className="lg:col-span-1">
            <div className="card premium-glow">
              <h2 className="text-lg font-medium text-light-purple-200 mb-4">
                Account Information
              </h2>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-light-purple-400">Account Type</p>
                  <p className="text-sm text-light-purple-200 capitalize">
                    {user?.role || 'User'}
                  </p>
                </div>

                <div>
                  <p className="text-sm font-medium text-light-purple-400">Member Since</p>
                  <p className="text-sm text-light-purple-200">
                    {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                  </p>
                </div>

                <div>
                  <p className="text-sm font-medium text-light-purple-400">Email Verified</p>
                  <p className="text-sm text-light-purple-200">
                    {user?.emailVerified ? 'Yes' : 'No'}
                  </p>
                </div>

                <div>
                  <p className="text-sm font-medium text-light-purple-400">Account Status</p>
                  <p className="text-sm text-light-purple-200">
                    {user?.isActive ? 'Active' : 'Inactive'}
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="card premium-glow mt-6">
              <h2 className="text-lg font-medium text-light-purple-200 mb-4">
                Quick Actions
              </h2>
              
              <div className="space-y-3">
                <button 
                  onClick={() => setShowPasswordModal(true)}
                  disabled={activeButton === 'changePassword'}
                  className={`w-full btn-secondary text-left transition-all duration-300 ${
                    activeButton === 'changePassword' 
                      ? 'bg-brinjal-100 border-brinjal-300 cursor-not-allowed' 
                      : 'hover:bg-brinjal-50 hover:border-brinjal-400'
                  }`}
                >
                  {activeButton === 'changePassword' ? (
                    <span className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brinjal-600 mr-2"></div>
                      Processing...
                    </span>
                  ) : (
                    'Change Password'
                  )}
                </button>
                
                <button 
                  onClick={handleDownloadOrderHistory}
                  disabled={activeButton === 'orderHistory'}
                  className={`w-full btn-secondary text-left transition-all duration-300 ${
                    activeButton === 'orderHistory' 
                      ? 'bg-brinjal-100 border-brinjal-300 cursor-not-allowed' 
                      : 'hover:bg-brinjal-50 hover:border-brinjal-400'
                  }`}
                >
                  {activeButton === 'orderHistory' ? (
                    <span className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brinjal-600 mr-2"></div>
                      Downloading...
                    </span>
                  ) : (
                    'Download Order History'
                  )}
                </button>
                
                <button 
                  onClick={handleExportProfileData}
                  disabled={activeButton === 'exportProfile'}
                  className={`w-full btn-secondary text-left transition-all duration-300 ${
                    activeButton === 'exportProfile' 
                      ? 'bg-brinjal-100 border-brinjal-300 cursor-not-allowed' 
                      : 'hover:bg-brinjal-50 hover:border-brinjal-400'
                  }`}
                >
                  {activeButton === 'exportProfile' ? (
                    <span className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brinjal-600 mr-2"></div>
                      Exporting...
                    </span>
                  ) : (
                    'Export Profile Data'
                  )}
                </button>
                
                <button 
                  onClick={() => setShowDeleteModal(true)}
                  disabled={activeButton === 'deleteAccount'}
                  className={`w-full btn-outline text-left transition-all duration-300 ${
                    activeButton === 'deleteAccount' 
                      ? 'bg-red-100 border-red-300 text-red-400 cursor-not-allowed' 
                      : 'text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-300'
                  }`}
                >
                  {activeButton === 'deleteAccount' ? (
                    <span className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                      Deleting...
                    </span>
                  ) : (
                    'Delete Account'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold mb-4">Change Password</h3>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  className="input-field"
                />
              </div>
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="flex-1 btn-secondary"
                  disabled={passwordLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary"
                  disabled={passwordLoading}
                >
                  {passwordLoading ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold mb-4 text-red-600">Delete Account</h3>
            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete your account? This action cannot be undone.
              </p>
              <div className="bg-red-50 border border-red-200 rounded p-4">
                <p className="text-sm text-red-800">
                  <strong>Warning:</strong> Deleting your account will:
                </p>
                <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                  <li>Permanently delete all your data</li>
                  <li>Cancel all active orders</li>
                  <li>Remove your profile information</li>
                  <li>You won't be able to recover this account</li>
                </ul>
              </div>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 btn-secondary"
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
